import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, LogOut, Home as HomeIcon } from "lucide-react";
import { ROUTES } from "../routes";
import { useAuth } from "../context/AuthContext";
import { api, money } from "../lib/api";
import Ticker from "./Ticker";
import { DemoWelcome, SampleBadge } from "./DemoData";
import Tour from "./Tour";
import { BetaBanner } from "./BetaBanner";
import { PreviewNudge } from "./PreviewBanner";
import { useFeatures } from "./Flagged";
import CoachDock from "./CoachDock";
import { VerifyBanner } from "../pages/AuthExtras";
import { Link } from "react-router-dom";
import { AppFooter } from "../pages/Legal";
import { ConsentGate } from "./ConsentGate";

export default function Shell() {
  const features = useFeatures();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [risk, setRisk] = useState(null);
  const [today, setToday] = useState(null);
  const loc = useLocation();

  useEffect(() => { setOpen(false); api.get("/risk/status").then((r) => setRisk(r.data)).catch(() => {}); api.get("/today").then((r) => setToday(r.data)).catch(() => {}); }, [loc.pathname]);

  let lastG = "";
  return (
    <div className="app">
      <aside className={`rail ${open ? "open" : ""}`} data-testid="sidebar">
        <div className="brand"><b>ExperTraQ</b><span>BETA</span></div>
        <SampleBadge />
        {risk && (
          <div className="rail-stats" data-testid="rail-stats">
            <div><div className="rail-k">Equity</div><div className="rail-v mono" data-testid="rail-equity">{money(risk.equity)}</div></div>
            <div><div className="rail-k">Daily loss budget</div>
              <div className="rail-v mono">{risk.daily.left_pct}% left <span className={`tag ${risk.locked ? "bad" : risk.daily.left_pct < 40 ? "warn" : "ok"}`}>{risk.locked ? "Locked" : "Open"}</span></div>
              <div className="meter" style={{ marginTop: 4 }}><i style={{ width: `${risk.daily.left_pct}%` }} /></div></div>
            <div><div className="rail-k">Drawdown vs limit</div><div className="rail-v mono">{risk.drawdown_pct}% / {risk.settings.max_drawdown_pct}%</div></div>
          </div>
        )}
        <nav className="nav" data-testid="main-nav">
          {ROUTES.filter((r) => (!r.admin || ["admin", "superadmin"].includes(user?.role)) && (!r.staff || ["admin", "superadmin"].includes(user?.role)) && (!r.flag || features?.[r.flag] || ["admin", "superadmin"].includes(user?.role))).map((r) => {
            const group = r.g !== lastG ? <div className="navgroup" key={"g" + r.g}>{r.g}</div> : null;
            lastG = r.g;
            return [group, <NavLink key={r.id} to={`/${r.id}`} data-testid={`nav-${r.id}`} className={({ isActive }) => (isActive ? "on" : "")}>{r.label}</NavLink>];
          })}
        </nav>
        <div style={{ marginTop: "auto", padding: "14px 10px 0", fontSize: ".78rem", color: "var(--ink-3)" }}>
          <div className="truncate" data-testid="user-email">{user?.name} · <span style={{ color: user?.plan === "free" ? "var(--ink-3)" : "var(--gold)" }}>{user?.plan}</span>{user?.preview_until && <span className="tag ok" style={{ marginLeft: 6 }} data-testid="rail-preview-tag">Team preview</span>}{user?.plan === "free" && <span className="tag warn" style={{ marginLeft: 6 }} data-testid="rail-delayed-tag">metrics delayed 2h</span>}</div>
          <Link to="/" className="btn ghost sm mt-2" style={{ marginRight: 6 }} data-testid="rail-home-link"><HomeIcon size={13} /> Home page</Link><button className="btn ghost sm mt-2" onClick={logout} data-testid="logout-btn"><LogOut size={13} /> Sign out</button>
        </div>
      </aside>
      <main className="main">
        <Ticker />
        <div className="topbar">
          <button className="btn ghost sm hamb" onClick={() => setOpen(!open)} data-testid="menu-toggle"><Menu size={14} /></button>
          <span>{new Date().toUTCString().slice(0, 22)} UTC</span>
          <span className="mono flex gap-2 items-center">expertraq.com · trade with evidence <BetaBanner compact /></span>
        </div>
        <VerifyBanner />
        {risk?.locked && (
          <div className="banner" data-testid="lockout-banner"><h3>Trading lockout active</h3>
            <p style={{ margin: "6px 0 0", fontSize: ".86rem" }}>{risk.lock_reason}</p>
            <p style={{ margin: "6px 0 0", fontSize: ".86rem", color: "var(--ink-2)" }}>The lockout lifts at <b className="mono">00:00 UTC</b>. Journaling, review, education and calculators stay open — that is the point of the pause.</p></div>
        )}
        {today?.streak_at_risk && loc.pathname !== "/journal" && <div className="nudge" data-testid="streak-nudge"><span><b>Your {today.streak}-day discipline streak is at risk.</b> Today's journal isn't complete yet — a two-minute review keeps it alive.</span><Link className="btn sm" to="/journal" data-testid="streak-nudge-btn">Write today's review</Link></div>}
        <ConsentGate><Outlet /></ConsentGate>
        <DemoWelcome />
        <Tour />
        <PreviewNudge until={user?.preview_until} />
        <CoachDock />
        <AppFooter />
      </main>
    </div>
  );
}
